// src/pages/AdhdScreeningPhoenix.js
import React, { useState, useEffect, useMemo } from 'react';
import { useForm } from "react-hook-form";
import { ChevronDownIcon, ChevronUpIcon, CheckCircleIcon } from '@heroicons/react/solid';
import { functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import DateOfBirthPicker from '../rms/components/DateOfBirthPicker';

// Local storage keys (defined outside component to avoid recreation)
const STORAGE_KEYS = {
    CURRENT_QUESTION: 'adhd_screening_current_question',
    SUBMITTED_ANSWERS: 'adhd_screening_submitted_answers',
    IS_COMPLETE: 'adhd_screening_is_complete',
    CURRENT_FORM_DATA: 'adhd_screening_current_form_data',
    WELCOME_COMPLETE: 'adhd_screening_welcome_complete'
};

// Utility: Format ISO date (YYYY-MM-DD) to "DD MMM YYYY" for human-readable emails
const formatISODateToDisplay = (isoDateString) => {
    if (!isoDateString) return '';
    try {
        const date = new Date(isoDateString);
        if (isNaN(date.getTime())) return '';
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    } catch (e) {
        return '';
    }
};

// ASRS Scoring Logic
const calculateASRSScore = (answers) => {
    // Questions where "Sometimes", "Often", or "Very Often" = +1 point
    const groupA = [1, 2, 3, 9, 12, 16, 18];
    // Questions where "Often" or "Very Often" = +1 point  
    const groupB = [4, 5, 6, 7, 8, 10, 11, 13, 14, 15, 17];
    
    let score = 0;
    
    answers.forEach(answer => {
        // Extract question number from asrs_X format
        const questionId = answer.questionId;
        if (!questionId || !questionId.startsWith('asrs_')) return;
        
        const questionNum = parseInt(questionId.replace('asrs_', ''));
        const response = answer.answer;
        
        if (groupA.includes(questionNum)) {
            // Group A: Sometimes, Often, or Very Often = +1
            if (['Sometimes', 'Often', 'Very Often'].includes(response)) {
                score += 1;
            }
        } else if (groupB.includes(questionNum)) {
            // Group B: Often or Very Often = +1
            if (['Often', 'Very Often'].includes(response)) {
                score += 1;
            }
        }
    });
    
    return score;
};

// Static question data (defined outside component to avoid recreation)
const personalQuestions = [
    {
        id: 'names',
        question: 'What is your name?',
        type: 'names',
        required: true,
        instruction: 'Please provide your name so we can include this in your report.',
        fields: [
            {
                id: 'firstName',
                label: 'First name',
                type: 'text',
                required: true
            },
            {
                id: 'lastName', 
                label: 'Last name',
                type: 'text',
                required: true
            }
        ]
    },
    {
        id: 'dob',
        question: 'What is your date of birth?',
        type: 'dob',
        required: true,
        // If the user declines an assessment, we will NOT include DOB in the submission payload.
        instruction: 'Please select your date of birth.'
    },
    {
        id: 'email',
        question: 'What is your email address?',
        type: 'email',
        required: true,
        instruction: 'Please provide your email address to receive your report.'
    }
];

const asrsQuestions = [
    "1 of 18: How often do you have trouble wrapping up the final details of a project, once the challenging parts have been done?",
    "2 of 18: How often do you have difficulty getting things in order when you have to do a task that requires organization?",
    "3 of 18: How often do you have problems remembering appointments or obligations?",
    "4 of 18: When you have a task that requires a lot of thought, how often do you avoid or delay getting started?",
    "5 of 18: How often do you fidget or squirm with your hands or feet when you have to sit down for a long time?",
    "6 of 18: How often do you feel overly active and compelled to do things, like you were driven by a motor?",
    "7 of 18: How often do you make careless mistakes when you have to work on a boring or difficult project?",
    "8 of 18: How often do you have difficulty keeping your attention when you are doing boring or repetitive work?",
    "9 of 18: How often do you have difficulty concentrating on what people say to you, even when they are speaking to you directly?",
    "10 of 18: How often do you misplace or have difficulty finding things at home or at work?",
    "11 of 18: How often are you distracted by activity or noise around you?",
    "12 of 18: How often do you leave your seat in meetings or other situations in which you are expected to remain seated?",
    "13 of 18: How often do you feel restless or fidgety?",
    "14 of 18: How often do you have difficulty unwinding and relaxing when you have time to yourself?",
    "15 of 18: How often do you find yourself talking too much when you are in social situations?",
    "16 of 18: When you're in a conversation, how often do you find yourself finishing the sentences of the people you are talking to, before they can finish them themselves?",
    "17 of 18: How often do you have difficulty waiting your turn in situations when turn taking is required?",
    "18 of 18: How often do you interrupt others when they are busy?"
];

const ratingOptions = [
    "Never",
    "Rarely", 
    "Sometimes",
    "Often",
    "Very Often"
];

export default function AdhdScreeningPhoenix() {
    const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm();
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [submittedAnswers, setSubmittedAnswers] = useState([]);
    const [isComplete, setIsComplete] = useState(false);
    const [showAnswerHistory, setShowAnswerHistory] = useState(false);
    const [welcomeComplete, setWelcomeComplete] = useState(false);
    const [currentWelcomeStep, setCurrentWelcomeStep] = useState(0);
    const [showScoreMessage, setShowScoreMessage] = useState(false);
    const [currentScore, setCurrentScore] = useState(0);
    const [showAssessmentRequest, setShowAssessmentRequest] = useState(false);
    const [assessmentChoice, setAssessmentChoice] = useState('');
    const [declineReason, setDeclineReason] = useState('');
    const [showResultBanner, setShowResultBanner] = useState(false);
    const [resultBannerType, setResultBannerType] = useState(''); // 'success' or 'error'
    const [resultBannerMessage, setResultBannerMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);


    // Combine all questions with new order: ASRS questions first, then personal info
    const allQuestions = useMemo(() => [
        ...asrsQuestions.map((question, index) => ({
            id: `asrs_${index + 1}`,
            question: question,
            type: 'rating',
            required: true
        })),
        ...personalQuestions
    ], []); // Empty deps since personalQuestions and asrsQuestions are now truly static (outside component)

    const currentQuestion = allQuestions[currentQuestionIndex];

    // Load saved progress on component mount
    useEffect(() => {
        const savedQuestionIndex = localStorage.getItem(STORAGE_KEYS.CURRENT_QUESTION);
        const savedAnswers = localStorage.getItem(STORAGE_KEYS.SUBMITTED_ANSWERS);
        const savedIsComplete = localStorage.getItem(STORAGE_KEYS.IS_COMPLETE);
        const savedFormData = localStorage.getItem(STORAGE_KEYS.CURRENT_FORM_DATA);
        const savedWelcomeComplete = localStorage.getItem(STORAGE_KEYS.WELCOME_COMPLETE);

        if (savedQuestionIndex !== null) {
            setCurrentQuestionIndex(parseInt(savedQuestionIndex, 10));
        }
        if (savedAnswers) {
            try {
                setSubmittedAnswers(JSON.parse(savedAnswers));
            } catch (e) {
                console.error('Error parsing saved answers:', e);
            }
        }
        if (savedIsComplete === 'true') {
            setIsComplete(true);
        }
        if (savedWelcomeComplete === 'true') {
            setWelcomeComplete(true);
        }
        if (savedFormData) {
            try {
                const formData = JSON.parse(savedFormData);
                // Restore form values for current question
                Object.keys(formData).forEach(key => {
                    // Need to handle text inputs specially for editability
                    const questionData = allQuestions.find(q => q.id === key);
                    if (questionData && (questionData.type === 'text' || questionData.type === 'email')) {
                        setValue(key, '');
                        setTimeout(() => {
                            setValue(key, formData[key], { 
                                shouldValidate: false,
                                shouldDirty: true 
                            });
                        }, 0);
                    } else {
                        setValue(key, formData[key]);
                    }
                });
            } catch (e) {
                console.error('Error parsing saved form data:', e);
            }
        }
    }, [setValue, allQuestions]); // setValue is stable from react-hook-form, allQuestions needed for question lookup

        // Restore current question's answer when currentQuestionIndex or submittedAnswers change
    useEffect(() => {
        if (currentQuestion) {
            const currentAnswer = submittedAnswers.find(a => a.questionIndex === currentQuestionIndex);
            if (currentAnswer) {
                // For text inputs, we need to handle restoration differently to ensure editability
                if (currentQuestion.type === 'text' || currentQuestion.type === 'email') {
                    // Clear first, then set to ensure proper form state
                    setValue(currentQuestion.id, '');
                    setTimeout(() => {
                        setValue(currentQuestion.id, currentAnswer.answer, { 
                            shouldValidate: false,
                            shouldDirty: true 
                        });
                    }, 0);
                } else {
                    // For radio buttons, restore directly
                    setValue(currentQuestion.id, currentAnswer.answer);
                }
            } else {
                // Clear the field if no answer exists
                setValue(currentQuestion.id, '');
            }
        }
    }, [currentQuestionIndex, submittedAnswers, currentQuestion, setValue]);

    // Save progress to localStorage whenever state changes
    useEffect(() => {
        localStorage.setItem(STORAGE_KEYS.CURRENT_QUESTION, currentQuestionIndex.toString());
    }, [currentQuestionIndex]);

    useEffect(() => {
        localStorage.setItem(STORAGE_KEYS.SUBMITTED_ANSWERS, JSON.stringify(submittedAnswers));
    }, [submittedAnswers]);

    useEffect(() => {
        localStorage.setItem(STORAGE_KEYS.IS_COMPLETE, isComplete.toString());
    }, [isComplete]);

    // Watch for form changes and save current input to localStorage
    const watchedValues = watch();
    useEffect(() => {
        if (currentQuestion && watchedValues[currentQuestion.id]) {
            const currentFormData = { [currentQuestion.id]: watchedValues[currentQuestion.id] };
            localStorage.setItem(STORAGE_KEYS.CURRENT_FORM_DATA, JSON.stringify(currentFormData));
        }
    }, [watchedValues, currentQuestion]);

    // Calculate ASRS score whenever answers change
    useEffect(() => {
        const asrsAnswers = submittedAnswers.filter(answer => {
            const questionData = allQuestions[answer.questionIndex];
            return questionData && questionData.id.startsWith('asrs_');
        }).map(answer => ({
            questionId: allQuestions[answer.questionIndex].id,
            answer: answer.answer
        }));
        
        const score = calculateASRSScore(asrsAnswers);
        setCurrentScore(score);
    }, [submittedAnswers, allQuestions]);

    // Calculate time estimates (15 seconds per question)
    const secondsPerQuestion = 15;
    const remainingQuestions = allQuestions.length - currentQuestionIndex - 1;
    const remainingSeconds = remainingQuestions * secondsPerQuestion;
    const remainingMinutes = Math.max(1, Math.ceil(remainingSeconds / 60));
    
    // Section calculations (new order: ASRS first, then personal info)
    const screeningQuestions = 18; // Questions 0-17 
    const personalInfoQuestions = 4; // Questions 18-21 (name, DOB, email)
    
    // Current section progress
    const currentSection = currentQuestionIndex < screeningQuestions ? 'screening' : 'personal';
    const screeningProgress = currentQuestionIndex >= screeningQuestions ? 100 : (currentQuestionIndex / screeningQuestions) * 100;
    const personalInfoProgress = currentSection === 'screening' ? 0 : ((currentQuestionIndex - screeningQuestions) / personalInfoQuestions) * 100;
    
    // Calculate remaining questions and time for each section
    const screeningQuestionsRemaining = currentSection === 'screening' 
        ? Math.max(0, screeningQuestions - currentQuestionIndex) 
        : 0;
    const personalInfoRemaining = currentSection === 'personal' 
        ? Math.max(0, personalInfoQuestions - (currentQuestionIndex - screeningQuestions))
        : personalInfoQuestions; // All personal info questions remain when in screening section
    
    const screeningQuestionsRemainingSeconds = screeningQuestionsRemaining * secondsPerQuestion;
    const personalInfoRemainingSeconds = personalInfoRemaining * secondsPerQuestion;
    
    // Format time display for each section
    const formatSectionTime = (seconds, isCompleted) => {
        if (isCompleted) {
            return 'Completed';
        }
        const minutes = Math.ceil(seconds / 60);
        if (minutes < 1) {
            return 'less than 1 min';
        }
        return `${minutes} min`;
    };

    const onSubmit = (data) => {
        // Create/update answer for current question
        let answerValue;
        
        // Handle special case for names question type
        if (currentQuestion.type === 'names') {
            // For names question, we store both firstName and lastName
            const firstName = data.firstName || '';
            const lastName = data.lastName || '';
            
            // Validate that both names are provided
            if (!firstName.trim() || !lastName.trim()) {
                console.error('Both first name and last name are required');
                return; // Don't proceed if names are missing
            }
            
            answerValue = {
                firstName: firstName.trim(),
                lastName: lastName.trim()
            };
            console.log('Names question submitted:', answerValue);
        } else {
            answerValue = data[currentQuestion.id];
        }
        
        const newAnswer = {
            question: currentQuestion.question,
            answer: answerValue,
            questionIndex: currentQuestionIndex
        };

        // Update answers array - replace if exists, add if new
        const updatedAnswers = [...submittedAnswers];
        const existingIndex = updatedAnswers.findIndex(a => a.questionIndex === currentQuestionIndex);
        
        if (existingIndex >= 0) {
            updatedAnswers[existingIndex] = newAnswer;
        } else {
            updatedAnswers.push(newAnswer);
        }
        
        setSubmittedAnswers(updatedAnswers);

        // Clear the form for the next question to prevent prefilling
        Object.keys(data).forEach(key => {
            setValue(key, '');
        });
        
        // Clear current form data from localStorage since we're moving to next question
        localStorage.removeItem(STORAGE_KEYS.CURRENT_FORM_DATA);

        // Check if we should show score message after first 6 ASRS questions
        if (currentQuestionIndex === 5) { // 0-indexed, so 5 = 6th question
            setShowScoreMessage(true);
            return; // Don't advance to next question yet
        }

        // Move to next question or complete
        if (currentQuestionIndex < allQuestions.length - 1) {
            const nextQuestionIndex = currentQuestionIndex + 1;
            const nextQuestion = allQuestions[nextQuestionIndex];
            
            setCurrentQuestionIndex(nextQuestionIndex);
            
            // Check if the next question already has an answer and restore it
            const nextAnswer = updatedAnswers.find(a => a.questionIndex === nextQuestionIndex);
            if (nextQuestion && nextAnswer) {
                // Restore the answer in the form
                setTimeout(() => {
                    if (nextQuestion.type === 'text' || nextQuestion.type === 'email') {
                        // For text inputs, clear first then set to ensure editability
                        setValue(nextQuestion.id, '');
                        setTimeout(() => {
                            setValue(nextQuestion.id, nextAnswer.answer, { 
                                shouldValidate: false,
                                shouldDirty: true 
                            });
                        }, 0);
                    } else {
                        setValue(nextQuestion.id, nextAnswer.answer);
                    }
                }, 0);
            }
        } else {
            // All questions completed - check threshold to determine next screen
            
            // Calculate score from ONLY the first 6 ASRS questions for threshold decision
            const first6AsrsAnswers = updatedAnswers.filter(answer => {
                const questionData = allQuestions[answer.questionIndex];
                if (!questionData || !questionData.id.startsWith('asrs_')) return false;
                
                // Extract question number and only include first 6 questions
                const questionNum = parseInt(questionData.id.replace('asrs_', ''));
                return questionNum >= 1 && questionNum <= 6;
            }).map(answer => ({
                questionId: allQuestions[answer.questionIndex].id,
                answer: answer.answer
            }));
            
            const first6Score = calculateASRSScore(first6AsrsAnswers);
            const meetsThreshold = first6Score >= 4;
            
            if (meetsThreshold) {
                // ≥4/6 threshold: Show assessment request screen
                setShowAssessmentRequest(true);
            } else {
                // <4/6 threshold: Skip assessment request, go directly to completion with "no" choice
                setAssessmentChoice('threshold-not-met');
                setIsComplete(true);
            }
            
            console.log('All answers:', updatedAnswers);
            console.log('First 6 ASRS score:', first6Score, '/6, Meets threshold:', meetsThreshold);
        }
    };

    const handlePrevious = () => {
        if (currentQuestionIndex > 0) {
            // Clear current form values
            setValue(currentQuestion.id, '');
            
            const newQuestionIndex = currentQuestionIndex - 1;
            const previousQuestion = allQuestions[newQuestionIndex];
            
            setCurrentQuestionIndex(newQuestionIndex);
            
            // Find and restore the answer for the question we're going back to
            const previousAnswer = submittedAnswers.find(a => a.questionIndex === newQuestionIndex);
            if (previousQuestion && previousAnswer) {
                // Restore the answer in the form
                setTimeout(() => {
                    if (previousQuestion.type === 'text' || previousQuestion.type === 'email') {
                        // For text inputs, clear first then set to ensure editability
                        setValue(previousQuestion.id, '');
                        setTimeout(() => {
                            setValue(previousQuestion.id, previousAnswer.answer, { 
                                shouldValidate: false,
                                shouldDirty: true 
                            });
                        }, 0);
                    } else {
                        setValue(previousQuestion.id, previousAnswer.answer);
                    }
                }, 0);
            }
        } else if (currentQuestionIndex === 0) {
            // Go back to the fees page (last step of welcome carousel)
            // Clear current form values but preserve all submitted answers
            setValue(currentQuestion.id, '');
            
            // Return to welcome carousel at the fees page
            setWelcomeComplete(false);
            setCurrentWelcomeStep(2); // Go to fees page (step 2)
            
            // Don't clear localStorage - we want to preserve all answers
            // The user can navigate forward again and see their previous answers
        }
    };

    // Welcome page handlers
    const handleWelcomeNext = () => {
        if (currentWelcomeStep < 2) {
            setCurrentWelcomeStep(currentWelcomeStep + 1);
        }
    };

    const handleWelcomeStart = () => {
        setWelcomeComplete(true);
        localStorage.setItem(STORAGE_KEYS.WELCOME_COMPLETE, 'true');
        
        // Restore current question's answer when returning from carousel
        setTimeout(() => {
            if (currentQuestion) {
                const currentAnswer = submittedAnswers.find(a => a.questionIndex === currentQuestionIndex);
                if (currentAnswer) {
                    console.log('Restoring answer after returning from carousel:', currentAnswer);
                    
                    // Handle different question types for restoration
                    if (currentQuestion.type === 'names' && typeof currentAnswer.answer === 'object') {
                        // For names question, restore both firstName and lastName
                        setValue('firstName', currentAnswer.answer.firstName || '');
                        setValue('lastName', currentAnswer.answer.lastName || '');
                    } else if (currentQuestion.type === 'text' || currentQuestion.type === 'email') {
                        // For text inputs, clear first then set to ensure editability
                        setValue(currentQuestion.id, '');
                        setTimeout(() => {
                            setValue(currentQuestion.id, currentAnswer.answer, { 
                                shouldValidate: false,
                                shouldDirty: true 
                            });
                        }, 0);
                    } else {
                        // For radio buttons, restore directly
                        setValue(currentQuestion.id, currentAnswer.answer);
                    }
                }
            }
        }, 100); // Small delay to ensure component has updated
    };

    const handleScoreMessageContinue = () => {
        setShowScoreMessage(false);
        setCurrentQuestionIndex(6); // Move to 7th question (0-indexed)
    };

    const handleAssessmentChoice = (choice) => {
        setAssessmentChoice(choice);
        setShowAssessmentRequest(false); // Hide assessment request screen
        setIsComplete(true); // Show completion screen
        // Clear localStorage when completing (ready for backend submission)
        localStorage.removeItem(STORAGE_KEYS.CURRENT_QUESTION);
        localStorage.removeItem(STORAGE_KEYS.SUBMITTED_ANSWERS);
        localStorage.removeItem(STORAGE_KEYS.IS_COMPLETE);
        localStorage.removeItem(STORAGE_KEYS.CURRENT_FORM_DATA);
    };

    const handleGoBackToAssessmentChoice = () => {
        setIsComplete(false);
        setShowAssessmentRequest(true);
        setAssessmentChoice('');
        setDeclineReason('');
        setShowResultBanner(false);
        setResultBannerType('');
        setResultBannerMessage('');
    };

    // Function to clear all stored data (useful for testing or reset)
    // const clearStoredData = () => {
    //     localStorage.removeItem(STORAGE_KEYS.CURRENT_QUESTION);
    //     localStorage.removeItem(STORAGE_KEYS.SUBMITTED_ANSWERS);
    //     localStorage.removeItem(STORAGE_KEYS.IS_COMPLETE);
    //     localStorage.removeItem(STORAGE_KEYS.CURRENT_FORM_DATA);
    //     localStorage.removeItem(STORAGE_KEYS.WELCOME_COMPLETE);
    //     setCurrentQuestionIndex(0);
    //     setSubmittedAnswers([]);
    //     setIsComplete(false);
    //     setWelcomeComplete(false);
    //     setCurrentWelcomeStep(0);
    //     setShowScoreMessage(false);
    //     setCurrentScore(0);
    //     setShowAssessmentRequest(false);
    //     setAssessmentChoice('');
    //     setDeclineReason('');
    //     setShowResultBanner(false);
    //     setResultBannerType('');
    //     setResultBannerMessage('');
    //     setIsSubmitting(false);
    //     // Clear form values
    //     Object.keys(watch()).forEach(key => setValue(key, ''));
    // };

    // Show welcome screens if not completed
    if (!welcomeComplete) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-yellow-50 flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 z-10">
                    {/* <button
                        onClick={clearStoredData}
                        className="text-xs bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded transition-colors duration-200"
                        title="Clear all stored data (for testing)"
                    >
                        Reset
                    </button> */}
                    <div className="flex items-center space-x-3">
                        <img 
                            src="/phoenix_logo.png" 
                            alt="Phoenix Mental Health Services" 
                            className="h-5 w-auto"
                        />
                    </div>
                </div>

                {/* Welcome Carousel */}
                <div className="flex-1 flex flex-col items-center justify-center px-6 pb-16">
                    <div className="w-full max-w-lg">
                        
                        {/* Step 1: Welcome */}
                        {currentWelcomeStep === 0 && (
                            <div className="text-center space-y-5" style={{ animation: 'fade-up 0.6s ease-out' }}>
                                <h1 className="text-xl font-bold mb-3" style={{ color: '#43213e' }}>
                                    Welcome to our Adult ADHD Screening (ASRS) Questionnaire
                                </h1>
                                
                                <div className="space-y-3 text-left">
                                    <p className="text-sm text-gray-700 leading-relaxed">
                                        <span className="font-semibold">Find out if you might have ADHD in just 5 minutes.</span>
                                    </p>
                                    
                                    <p className="text-sm text-gray-700 leading-relaxed">
                                        This short, science-based quiz is completely free and can help you (and us) understand if an adult ADHD assessment could be right for you.
                                    </p>

                                    <p className="text-sm text-gray-700 leading-relaxed">
                                        When you’re done, we’ll email your results. You can then decide if you’d like to book an assessment with us or not. It’s completely up to you.
                                    </p>
                                    
                                    <p className="text-sm text-gray-700 leading-relaxed">
                                        Everything you share is kept <span className="font-semibold">private and confidential</span>.
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Step 2: About Us */}
                        {currentWelcomeStep === 1 && (
                            <div className="text-left space-y-5" style={{ animation: 'fade-up 0.6s ease-out' }}>
                                <div className="space-y-3">
                                    <h2 className="text-lg font-semibold" style={{ color: '#43213e' }}>
                                        About us
                                    </h2>
                                    <p className="text-sm text-gray-700 leading-relaxed">
                                        At Phoenix Mental Health Services, we offer specialist private ADHD assessments for people over the age of 18, delivered by experienced clinicians.
                                    </p>
                                    <p className="text-sm text-gray-700 leading-relaxed">
                                        Based in Wooburn Green, Buckinghamshire, we also provide nationwide remote assessments, making high-quality care accessible wherever you are in the UK.
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Step 3: Fees */}
                        {currentWelcomeStep === 2 && (
                            <div className="text-left space-y-5" style={{ animation: 'fade-up 0.6s ease-out' }}>
                                <div className="space-y-3">
                                    <h2 className="text-lg font-semibold" style={{ color: '#43213e' }}>
                                        Fees
                                    </h2>
                                    <p className="text-sm text-gray-700 leading-relaxed mb-3">
                                        Our full ADHD assessment service costs a one-off fee of <span className="font-semibold">£1,395</span> for 2026. This includes:
                                    </p>
                                    <ul className="text-sm text-gray-700 leading-relaxed ml-4 space-y-1">
                                        <li>• A full diagnostic evaluation</li>
                                        <li>• A formal diagnosis (if ADHD is identified)</li>
                                        <li>• Your first prescription, if it’s the right option for you and you choose to start medication</li>
                                        <li>• A free ADHD coaching session (worth £70) to help you after your diagnosis</li>
                                    </ul>
                                    <p className="text-sm text-gray-700 leading-relaxed">
                                        <span className="font-semibold">We also welcome patients with BUPA cover.</span> We aim to make support accessible, and flexible payment options through Klarna are available if needed.
                                    </p>
                                    <p className="text-sm text-gray-600 leading-relaxed italic">
                                        At Phoenix, we’re here to provide clear, compassionate, and personalised support, every step of the way.
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Progress Dots */}
                        <div className="flex justify-center mt-6 space-x-2">
                            {[0, 1, 2].map((step) => (
                                <div
                                    key={step}
                                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                                        step === currentWelcomeStep ? 'scale-125' : 'opacity-50'
                                    }`}
                                    style={{ 
                                        backgroundColor: step === currentWelcomeStep ? '#e7c22f' : '#43213e'
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                {/* CTA Button */}
                <div className="fixed bottom-0 left-0 right-0 backdrop-blur-sm p-4 max-w-lg mx-auto w-full">
                    <button
                        onClick={currentWelcomeStep < 2 ? handleWelcomeNext : handleWelcomeStart}
                        className="w-full font-semibold py-3 px-6 rounded-lg transition-all duration-200 hover:scale-105"
                        style={{ 
                            backgroundColor: '#43213e',
                            color: 'white'
                        }}
                    >
                        {currentWelcomeStep < 2 ? 'Next' : 'Start'}
                    </button>
                </div>

                {/* CSS Animations */}
                <style>{`
                    @keyframes fade-up {
                        0% { opacity: 0; transform: translateY(20px); }
                        100% { opacity: 1; transform: translateY(0); }
                    }
                `}</style>
            </div>
        );
    }

    // Show score message after first 6 questions
    if (showScoreMessage) {
        const scoreFromFirst6 = currentScore; // Based on answers to first 6 questions
        const hasPositiveIndication = scoreFromFirst6 >= 4;
        
        return (
            <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-yellow-50 flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 z-10">
                    {/* <button
                        onClick={clearStoredData}
                        className="text-xs bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded transition-colors duration-200"
                        title="Clear all stored data (for testing)"
                    >
                        Reset
                    </button> */}
                    <div className="flex items-center space-x-3">
                        <img 
                            src="/phoenix_logo.png" 
                            alt="Phoenix Mental Health Services" 
                            className="h-5 w-auto"
                        />
                    </div>
                </div>

                {/* Score Message Content */}
                <div className="flex-1 flex items-center justify-center px-4">
                    <div className="max-w-lg w-full text-center">
                        {/* Progress Icon */}
                        {/* <div 
                            className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-8"
                            style={{ 
                                backgroundColor: hasPositiveIndication ? '#e7c22f' : '#43213e',
                                animation: 'fade-in 0.8s ease-out 0.3s both'
                            }}
                        >
                            <span className="text-white text-xl font-bold">6/18</span>
                        </div>

                        {/* Title */}
                        {/* <h1 
                            className="text-xl font-bold mb-8 tracking-tight"
                            style={{ 
                                color: '#43213e',
                                animation: 'fade-up 0.8s ease-out 0.5s both'
                            }}
                        >
                            Initial Assessment Complete
                        </h1> 
                         */}

                        {/* What Happens Next Card */}
                        <div 
                            className="p-8 text-left"
                            style={{ 
                                animation: 'fade-up 0.8s ease-out 0.7s both',
                                // boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)'
                            }}
                        >                                                        
                            <div className="space-y-4">
                                <div 
                                    className="text-gray-700 leading-relaxed"
                                    style={{ animation: 'fade-slide-in 0.3s ease-out 0.6s both' }}
                                >
                                    {hasPositiveIndication ? (
                                        <p>
                                            Your answers suggest <span className="font-semibold">you might have ADHD.</span>
                                        </p>
                                    ) : (
                                        <p>
                                            <span className="font-semibold">Your initial answers suggest that ADHD may be less likely.</span>
                                        </p>
                                    )}
                                </div>
                                
                                <div 
                                    className="text-gray-700 leading-relaxed"
                                    style={{ animation: 'fade-slide-in 0.3s ease-out 0.9s both' }}
                                >
                                    <p>
                                        To get a fuller picture of your experiences, please continue by answering the next 12 questions.  
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Continue Button */}
                <div className="fixed bottom-0 left-0 right-0 backdrop-blur-sm p-4 max-w-lg mx-auto w-full">
                    <button
                        onClick={handleScoreMessageContinue}
                        className="w-full font-semibold py-3 px-6 rounded-lg transition-all duration-200 hover:scale-105"
                        style={{ 
                            backgroundColor: '#43213e',
                            color: 'white'
                        }}
                    >
                        Continue
                    </button>
                </div>

                {/* CSS Animations */}
                <style>{`
                    @keyframes fade-up {
                        0% { opacity: 0; transform: translateY(20px); }
                        100% { opacity: 1; transform: translateY(0); }
                    }
                `}</style>
            </div>
        );
    }

    // Show assessment request screen after all questions completed
    if (showAssessmentRequest) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-yellow-50 flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 z-10">
                    {/* <button
                        onClick={clearStoredData}
                        className="text-xs bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded transition-colors duration-200"
                        title="Clear all stored data (for testing)"
                    >
                        Reset
                    </button> */}
                    <div className="flex items-center space-x-3">
                        <img 
                            src="/phoenix_logo.png" 
                            alt="Phoenix Mental Health Services" 
                            className="h-5 w-auto"
                        />
                    </div>
                </div>

                {/* Assessment Request Content */}
                <div className="flex-1 flex items-center justify-center px-4">
                    <div className="max-w-lg w-full text-center">
                        {/* Title */}
                        <h1 
                            className="text-2xl font-bold mb-8 tracking-tight"
                            style={{ 
                                color: '#43213e',
                                animation: 'fade-up 0.8s ease-out 0.5s both'
                            }}
                        >
                            Next Steps
                        </h1>

                        {/* What Happens Next Card */}
                        <div 
                            className="text-sm p-8 text-left mb-8"
                            style={{ 
                                animation: 'fade-up 0.8s ease-out 0.7s both',
                                // boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)'
                            }}
                        >
                            <div className="space-y-4 text-left">
                                <div 
                                    className="text-gray-700 leading-relaxed"
                                    style={{ animation: 'fade-slide-in 0.3s ease-out 0.6s both' }}
                                >
                                    <p>
                                        Your screening score suggests that an ADHD assessment could be helpful.
                                    </p>
                                </div>
                                
                                <div 
                                    className="text-gray-700 leading-relaxed"
                                    style={{ animation: 'fade-slide-in 0.3s ease-out 0.9s both' }}
                                >
                                    <p>
                                        If you'd like to continue, we'll contact you within 2 working days with the next steps. First, tell us: will you pay privately or use BUPA insurance?
                                    </p>
                                </div>

                                <div 
                                    className="text-gray-700 leading-relaxed"
                                    style={{ animation: 'fade-slide-in 0.3s ease-out 1.2s both' }}
                                >
                                    <p>
                                        Choose an option below to receive your <span className="font-semibold">free ADHD screening results</span> and start your journey.
                                    </p>
                                </div>                                                            
                            </div>
                        </div>

                        {/* Request Question */}
                        <div 
                            className="mb-6"
                            style={{ animation: 'fade-up 0.8s ease-out 1.9s both' }}
                        >
                            <h2 className="text-xl font-semibold mb-6" style={{ color: '#43213e' }}>
                                Request an assessment with us?
                            </h2>
                        </div>

                        {/* CTA Buttons */}
                        <div 
                            className="space-y-3 text-sm"
                            style={{ animation: 'fade-up 0.8s ease-out 2.1s both' }}
                        >
                            <button
                                onClick={() => handleAssessmentChoice('bupa')}
                                className="w-full font-semibold py-3 px-6 rounded-lg transition-all duration-200 hover:scale-105"
                                style={{ 
                                    backgroundColor: '#43213e',
                                    color: 'white'
                                }}
                            >
                                Yes - I have BUPA Health Insurance
                            </button>
                            <button
                                onClick={() => handleAssessmentChoice('self-funding')}
                                className="w-full font-semibold py-3 px-6 rounded-lg transition-all duration-200 hover:scale-105"
                                style={{ 
                                    backgroundColor: '#43213e',
                                    color: 'white'
                                }}
                            >
                                Yes - I will pay for my own assessment (fee £1,395)
                            </button>
                            {/* Payment method logos — purely informational to reassure users about accepted payment options */}
                            <div className="flex flex-wrap items-center justify-center gap-4 my-1 pointer-events-none select-none">
                                <img src="/amex_logo.svg" alt="American Express accepted" className="h-8 w-auto opacity-90" draggable="false" />
                                <img src="/mastercard_logo.svg" alt="Mastercard accepted" className="h-6 w-auto opacity-90" draggable="false" />
                                <img src="/klarna_logo.svg" alt="Klarna available" className="h-6 w-auto opacity-90" draggable="false" />
                            </div>
                            <button
                                onClick={() => handleAssessmentChoice('no')}
                                className="w-full font-semibold py-3 px-6 rounded-lg transition-all duration-200 hover:scale-105 bg-gray-300 hover:bg-gray-400"
                            >
                                No thank you
                            </button>
                        </div>
                    </div>
                </div>

                {/* CSS Animations */}
                <style>{`
                    @keyframes fade-in {
                        0% { opacity: 0; }
                        100% { opacity: 1; }
                    }
                    @keyframes fade-up {
                        0% { opacity: 0; transform: translateY(30px); }
                        100% { opacity: 1; transform: translateY(0); }
                    }
                    @keyframes fade-slide-in {
                        0% { opacity: 0; transform: translateX(-20px); }
                        100% { opacity: 1; transform: translateX(0); }
                    }
                `}</style>
            </div>
        );
    }

    if (isComplete) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-yellow-50 flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 z-10 relative">
                    {/* <button
                        onClick={clearStoredData}
                        className="text-xs bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded-lg transition-all duration-200 hover:scale-105"
                        title="Clear all stored data (for testing)"
                    >
                        Reset
                    </button> */}
                    <div className="flex items-center space-x-3">
                        <img 
                            src="/phoenix_logo.png" 
                            alt="Phoenix Mental Health Services" 
                            className="h-5 w-auto"
                        />
                    </div>
                </div>

                {/* Main Content */}
                <div className="flex-1 flex items-center justify-center px-4">
                    <div className="max-w-lg w-full text-center">
                        {/* Simple Success Icon */}
                        <div className="mb-8">
                            <CheckCircleIcon 
                                className="w-28 h-28 mx-auto"
                                style={{ 
                                    color: '#e7c22f',
                                    animation: 'fade-in 1s ease-out'
                                }}
                            />
                        </div>

                        {/* Title */}
                        <h1 
                            className="text-4xl font-bold mb-8 tracking-tight"
                            style={{ 
                                color: '#43213e',
                                animation: 'fade-up 0.8s ease-out 0.5s both'
                            }}
                        >
                            Screening Results
                        </h1>
                        
                        {/* Hide content when success banner is showing */}
                        {!(showResultBanner && resultBannerType === 'success') && (
                            <>
                                {/* What Happens Next Card */}
                                <div 
                            className="p-8 text-left"
                            style={{ 
                                animation: 'fade-up 0.8s ease-out 0.7s both',
                                // boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)'
                            }}
                        >
                            <h2 className="text-xl font-semibold mb-6" style={{ color: '#43213e' }}>
                                What next?
                            </h2>
                            
                            <div className="text-sm space-y-4 text-left">
                                {assessmentChoice === 'threshold-not-met' ? (
                                    // Content for users who didn't meet threshold (bypassed assessment choice)
                                    <>
                                        <div 
                                            className="text-gray-700 leading-relaxed"
                                            style={{ animation: 'fade-slide-in 0.3s ease-out 0.6s both' }}
                                        >
                                            <p>
                                                <span className="font-semibold">Your screening results suggest ADHD is less likely.</span> Your results didn’t reach the level we usually need to offer an ADHD assessment.
                                            </p>
                                        </div>

                                        <div 
                                            className="text-gray-700 leading-relaxed"
                                            style={{ animation: 'fade-slide-in 0.3s ease-out 0.9s both' }}
                                        >
                                            <p className="mb-3">
                                                However, you're very welcome to get in touch. We offer a wide range of mental health services and may still be able to help you.
                                            </p>
                                            <ul className="text-sm text-gray-700 leading-relaxed ml-4 space-y-1">
                                                <li>📧 Contact us at <span className="font-semibold">enquiries@phoenix-mhs.com</span></li>
                                                <li>📞 Or call us on <span className="font-semibold">0370 162 0673</span></li>
                                            </ul>
                                        </div>
                                        
                                        <div 
                                            className="text-gray-700 leading-relaxed"
                                            style={{ animation: 'fade-slide-in 0.3s ease-out 1.2s both' }}
                                        >
                                            <p>
                                                Click <span className="font-semibold">'Finish and send me my results'</span> below to receive your full report by email.
                                            </p>
                                        </div>
                                    </>
                                ) : assessmentChoice === 'no' ? (
                                    // Content for declined assessment (chose "No thank you")
                                    <>
                                        <div 
                                            className="text-gray-700 leading-relaxed"
                                            style={{ animation: 'fade-slide-in 0.3s ease-out 0.6s both' }}
                                        >
                                            <p>
                                                You have chosen not to request an assessment with us, so we will not contact you.
                                            </p>
                                        </div>
                                                                                                                    
                                        <div 
                                            className="text-gray-700 leading-relaxed"
                                            style={{ animation: 'fade-slide-in 0.3s ease-out 0.9s both' }}
                                        >
                                            <p className="mb-4">
                                                Before leaving, please let us know why you have chosen not to proceed with an assessment, and then click 'Finish and send me my results' below to receive your full report by email.
                                            </p>
                                            <textarea
                                                value={declineReason}
                                                onChange={(e) => setDeclineReason(e.target.value)}
                                                placeholder="Please share your reason..."
                                                className={`w-full p-3 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200`}
                                                rows="4"
                                                required
                                                autoFocus
                                            />
                                            
                                        </div>
                                    </>
                                ) : (
                                    // Content for accepted assessment (BUPA or self-funding)
                                    <>
                                        <div 
                                            className="text-gray-700 leading-relaxed"
                                            style={{ animation: 'fade-slide-in 0.3s ease-out 0.6s both' }}
                                        >
                                            <p>
                                                Click the 'Finish and send me my results' button below to complete your screening process.
                                            </p>
                                        </div>
                                        
                                        <div 
                                            className="text-gray-700 leading-relaxed"
                                            style={{ animation: 'fade-slide-in 0.3s ease-out 0.9s both' }}
                                        >
                                            <p>
                                                Once you do this, you will receive your full report by email and we will review your results and contact you within two working days with the next steps.
                                            </p>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Send Results Button */} 
                        <div 
                            className="mt-4"
                            style={{ animation: 'fade-up 0.8s ease-out 1.9s both' }}
                        >                            
                            <button
                                onClick={async () => {
                                    if (isSubmitting) return; // Prevent double submission
                                    
                                    try {
                                        // Set loading state
                                        setIsSubmitting(true);
                                        
                                        // Hide any existing banner
                                        setShowResultBanner(false);
                                        
                                        // Extract first name, last name, email and date of birth
                                        let firstName = '';
                                        let lastName = '';
                                        let email = '';
                                        let dateOfBirth = ''; // ISO 'YYYY-MM-DD' from DOB picker

                                        // First, try to get from current form values (for when we're currently on that page)
                                        const currentFormValues = watch();
                                        firstName = currentFormValues.firstName || '';
                                        lastName = currentFormValues.lastName || '';
                                        email = currentFormValues.email || '';
                                        dateOfBirth = currentFormValues.dob || '';

                                        // Then try to get from submitted answers
                                        submittedAnswers.forEach(answer => {
                                            const questionIndex = answer.questionIndex;
                                            const question = allQuestions[questionIndex];
                                            
                                            if (question && question.id === 'email') {
                                                email = email || answer.answer;
                                            } else if (question && question.id === 'names' && answer.answer) {
                                                // Handle the names object structure
                                                if (typeof answer.answer === 'object') {
                                                    firstName = firstName || answer.answer.firstName || '';
                                                    lastName = lastName || answer.answer.lastName || '';
                                                }
                                            } else if (question && question.id === 'dob') {
                                                dateOfBirth = dateOfBirth || answer.answer || '';
                                            }
                                        });

                                        // Try to extract from localStorage for names (they get stored individually)
                                        const allStorageKeys = Object.keys(localStorage);
                                        allStorageKeys.forEach(key => {
                                            if (key.includes('adhd_screening')) {
                                                try {
                                                    const value = localStorage.getItem(key);
                                                    if (value) {
                                                        const parsed = JSON.parse(value);
                                                        // Check if this storage item has our personal info fields
                                                        if (typeof parsed === 'object' && parsed !== null) {
                                                            if (parsed.firstName && !firstName) firstName = parsed.firstName;
                                                            if (parsed.lastName && !lastName) lastName = parsed.lastName;
                                                            if (parsed.email && !email) email = parsed.email;
                                                            if (parsed.dob && !dateOfBirth) dateOfBirth = parsed.dob;
                                                        }
                                                    }
                                                } catch (e) {
                                                    // Skip non-JSON values
                                                }
                                            }
                                        });

                                        // Last resort: try to get from form register values
                                        if (!firstName || !lastName || !email || !dateOfBirth) {
                                            try {
                                                const formState = watch();
                                                if (formState) {
                                                    Object.keys(formState).forEach(key => {
                                                        if (key === 'firstName' && formState[key] && !firstName) {
                                                            firstName = formState[key];
                                                        }
                                                        if (key === 'lastName' && formState[key] && !lastName) {
                                                            lastName = formState[key];
                                                        }
                                                        if (key === 'email' && formState[key] && !email) {
                                                            email = formState[key];
                                                        }
                                                        if (key === 'dob' && formState[key] && !dateOfBirth) {
                                                            dateOfBirth = formState[key];
                                                        }
                                                    });
                                                }
                                            } catch (e) {
                                                console.error('Error accessing form state:', e);
                                            }
                                        }

                                        console.log('Extracted personal info:', { firstName, lastName, email, dateOfBirth });

                                        // Prepare data for submission
                                        // Only include DOB when the user positively opts to proceed (exclude for 'no' and 'threshold-not-met')
                                        const includeDob = assessmentChoice === 'bupa' || assessmentChoice === 'self-funding';
                                        const dateOfBirthFormatted = includeDob && dateOfBirth ? formatISODateToDisplay(dateOfBirth) : '';
                                        
                                        // Filter out DOB from answers when user declines or threshold not met (privacy)
                                        const filteredSubmittedAnswers = submittedAnswers.filter(a => {
                                            const q = allQuestions[a.questionIndex];
                                            if (!q) return true;
                                            if (!includeDob && q.id === 'dob') return false;
                                            return true;
                                        });
                                        const submissionData = {
                                            answers: filteredSubmittedAnswers.map(answer => ({
                                                questionId: allQuestions[answer.questionIndex].id,
                                                question: answer.question,
                                                answer: answer.answer,
                                                questionIndex: answer.questionIndex
                                            })),
                                            firstName,
                                            lastName,
                                            email,
                                            // Only include dateOfBirth fields when assessment choice is positive (BUPA or self-funding)
                                            ...(includeDob && dateOfBirth ? { dateOfBirth, dateOfBirthFormatted } : {}),
                                            assessmentChoice,
                                            declineReason: assessmentChoice === 'no' ? declineReason : '',
                                            organisation: 'Phoenix Mental Health Services' // 'ThinkDivergent'
                                        };

                                        console.log('Submitting ADHD screening:', submissionData);

                                        // Call the cloud function using Firebase Functions SDK
                                        const submitScreening = httpsCallable(functions, 'submitAdhdScreening');
                                        const result = await submitScreening(submissionData);
                                        console.log('Submission successful:', result.data);

                                        // Clear localStorage for privacy but keep user on completion screen
                                        localStorage.removeItem(STORAGE_KEYS.CURRENT_QUESTION);
                                        localStorage.removeItem(STORAGE_KEYS.SUBMITTED_ANSWERS);
                                        localStorage.removeItem(STORAGE_KEYS.IS_COMPLETE);
                                        localStorage.removeItem(STORAGE_KEYS.CURRENT_FORM_DATA);

                                        // Show success banner
                                        setResultBannerType('success');
                                        setResultBannerMessage('Your ADHD screening results have been sent to your email address');
                                        setShowResultBanner(true);

                                    } catch (error) {
                                        console.error('Error submitting ADHD screening:', error);
                                        
                                        // Show error banner
                                        setResultBannerType('error');
                                        setResultBannerMessage('There was an error submitting your screening. Please try again or contact support@thinkdivergent.co.uk');
                                        setShowResultBanner(true);
                                    } finally {
                                        // Always reset loading state
                                        setIsSubmitting(false);
                                    }
                                }}
                                disabled={isSubmitting || (assessmentChoice === 'no' && !declineReason.trim())}
                                className={`w-full font-semibold py-3 px-6 rounded-lg transition-all duration-300 flex items-center justify-center ${
                                    isSubmitting || (assessmentChoice === 'no' && !declineReason.trim())
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-60'
                                        : 'hover:scale-105 shadow-lg hover:shadow-xl'
                                }`}
                                style={{ 
                                    backgroundColor: isSubmitting || (assessmentChoice === 'no' && !declineReason.trim()) 
                                        ? undefined 
                                        : '#43213e',
                                    color: isSubmitting || (assessmentChoice === 'no' && !declineReason.trim()) 
                                        ? undefined 
                                        : 'white'
                                }}
                            >
                                {isSubmitting ? (
                                    <>
                                        {/* Loading spinner */}
                                        <svg 
                                            className="animate-spin -ml-1 mr-3 h-5 w-5" 
                                            xmlns="http://www.w3.org/2000/svg" 
                                            fill="none" 
                                            viewBox="0 0 24 24"
                                        >
                                            <circle 
                                                className="opacity-25" 
                                                cx="12" 
                                                cy="12" 
                                                r="10" 
                                                stroke="currentColor" 
                                                strokeWidth="4"
                                            ></circle>
                                            <path 
                                                className="opacity-75" 
                                                fill="currentColor" 
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                            ></path>
                                        </svg>
                                        Sending you your results...
                                    </>
                                ) : (
                                    'Finish and send me my results'
                                )}
                            </button>
                        </div>

                                {/* Back Button - only show for users who made an assessment choice */}
                                {!showResultBanner && assessmentChoice !== 'threshold-not-met' && (
                                    <div 
                                        className="mt-6"
                                        style={{ animation: 'fade-up 0.8s ease-out 0.3s both' }}
                                    >
                                        <button
                                            onClick={handleGoBackToAssessmentChoice}
                                            className="text-sm text-gray-600 hover:text-gray-800 underline transition-colors duration-200"
                                        >
                                            ... or go back and change your choice
                                        </button>
                                    </div>
                                )}
                            </>
                        )}

                        {/* Result Banner */}
                        {showResultBanner && (
                            <div 
                                className={`mt-6 p-4 rounded-lg border ${
                                    resultBannerType === 'success' 
                                        ? 'bg-green-50 border-green-200' 
                                        : 'bg-red-50 border-red-200'
                                }`}
                                style={{ 
                                    animation: 'fade-slide-up 0.5s ease-out',
                                    opacity: showResultBanner ? 1 : 0
                                }}
                            >
                                <div className="flex items-center">
                                    {resultBannerType === 'success' ? (
                                        <svg className="w-5 h-5 text-green-600 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    ) : (
                                        <svg className="w-5 h-5 text-red-600 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    )}
                                    <p className={`text-sm font-medium ${
                                        resultBannerType === 'success' ? 'text-green-800' : 'text-red-800'
                                    }`}>
                                        {resultBannerMessage}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* CSS Animations */}
                <style>{`
                    @keyframes fade-in {
                        0% { opacity: 0; }
                        100% { opacity: 1; }
                    }
                    @keyframes fade-up {
                        0% { opacity: 0; transform: translateY(30px); }
                        100% { opacity: 1; transform: translateY(0); }
                    }
                    @keyframes fade-slide-in {
                        0% { opacity: 0; transform: translateX(-20px); }
                        100% { opacity: 1; transform: translateX(0); }
                    }
                    @keyframes fade-slide-up {
                        0% { opacity: 0; transform: translateY(20px); }
                        100% { opacity: 1; transform: translateY(0); }
                    }
                `}</style>
            </div>
        );
    }

            return (
            <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-yellow-50 flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 z-10">
                    {/* <button
                        onClick={clearStoredData}
                        className="text-xs bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded transition-colors duration-200"
                        title="Clear all stored data (for testing)"
                    >
                        Reset
                    </button> */}
                    <div className="flex items-center space-x-3">
                        <img 
                            src="/phoenix_logo.png" 
                            alt="Phoenix Mental Health Services" 
                            className="h-6 w-auto"
                        />
                    </div>
                </div>

                {/* Scrollable Main Content */}
                <div className="flex-1 overflow-y-auto px-4 pb-20 pt-2">
                    <div className="max-w-lg mx-auto w-full">                

                        {/* Question Card */}
                        <div className="mb-4">
                    <form onSubmit={handleSubmit(onSubmit)} className="p-4">
                        {/* Question Title */}
                        <h2 className="text-base font-bold text-gray-900 mb-3 leading-snug text-left">
                            {currentQuestion.question}
                        </h2>

                        {/* Input Fields */}
                        {currentQuestion.type === 'names' ? (
                            <div className="mb-4">
                                {/* Instruction for names if any */}
                                {currentQuestion.instruction && (
                                    <p className="text-gray-600 text-sm mb-3 text-left">{currentQuestion.instruction}</p>
                                )}
                                <div className="space-y-3">
                                    {currentQuestion.fields.map((field) => (
                                        <div key={field.id}>
                                            <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                                                {field.label}
                                            </label>
                                            <input
                                                type={field.type}
                                                className="w-full p-3 border border-gray-200 rounded-xl bg-white text-gray-900 text-base placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200"
                                                style={{ 
                                                    '--tw-ring-color': '#e7c22f',
                                                    '--tw-ring-opacity': '0.5'
                                                }}
                                                placeholder={`Enter your ${field.label.toLowerCase()}`}
                                                {...register(field.id, { 
                                                    required: field.required ? `${field.label} is required` : false
                                                })}
                                                aria-label={field.label}
                                            />
                                            {errors[field.id] && (
                                                <p className="text-red-500 text-sm mt-1 text-left">{errors[field.id].message}</p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : currentQuestion.type === 'dob' ? (
                            <div className="mb-4">
                                {/* Instruction for DOB if any */}
                                {currentQuestion.instruction && (
                                    <p className="text-gray-600 text-sm mb-3 text-left">{currentQuestion.instruction}</p>
                                )}
                                {/* 
                                    Date of Birth is managed via a controlled component. We bind it to react-hook-form
                                    using a hidden input for validation and setValue/watch for state updates.
                                    The component emits an ISO string (YYYY-MM-DD) once all parts are valid.
                                */}
                                <DateOfBirthPicker
                                    value={watch('dob') || ''}
                                    onChange={(val) => {
                                        // Persist value in form state and trigger validation so the user cannot proceed without DOB
                                        setValue('dob', val, { shouldValidate: true, shouldDirty: true });
                                    }}
                                    minAgeYears={18}
                                    maxAgeYears={120}
                                />
                                {/* Hidden input to hook into RHF validation lifecycle */}
                                <input
                                    type="hidden"
                                    value={watch('dob') || ''}
                                    {...register('dob', { 
                                        required: currentQuestion.required ? 'Please select your date of birth' : false 
                                    })}
                                />
                                {errors['dob'] && (
                                    <p className="text-red-500 text-sm mt-2 text-left">{errors['dob'].message}</p>
                                )}
                            </div>
                        ) : currentQuestion.type === 'text' || currentQuestion.type === 'email' ? (
                            <div className="mb-4">
                                {/* Instruction text */}
                                {currentQuestion.instruction && (
                                    <p className="text-gray-600 text-sm mb-3 text-left">{currentQuestion.instruction}</p>
                                )}
                                <input
                                    type={currentQuestion.type}
                                    className="w-full p-3 border border-gray-200 rounded-xl bg-white text-gray-900 text-base placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200"
                                    style={{ 
                                        '--tw-ring-color': '#e7c22f',
                                        '--tw-ring-opacity': '0.5'
                                    }}
                                    placeholder={
                                        currentQuestion.type === 'email' 
                                            ? 'Enter your email address' 
                                            : `Enter your ${currentQuestion.question.toLowerCase().replace('what is your ', '').replace('?', '')}`
                                    }
                                    {...register(currentQuestion.id, { 
                                        required: currentQuestion.required ? 'This field is required' : false,
                                        pattern: currentQuestion.type === 'email' ? {
                                            value: /^\S+@\S+$/i,
                                            message: 'Please enter a valid email address'
                                        } : undefined
                                    })}
                                />
                                {errors[currentQuestion.id] && (
                                    <p className="text-red-500 text-sm mt-2 text-left">{errors[currentQuestion.id].message}</p>
                                )}
                            </div>
                        ) : (
                            <div className="mb-4">
                                <p className="text-gray-500 text-sm mb-2 italic text-left">Please select one.</p>
                                <div className="space-y-1.5">
                                    {ratingOptions.map((option) => {
                                        const currentValue = watch(currentQuestion.id);
                                        const isSelected = currentValue === option;

                                        return (
                                            <label
                                                key={option}
                                                className={`
                                                    flex items-center p-2.5 rounded-lg border-2 cursor-pointer transition-all duration-300 ease-out
                                                    ${isSelected
                                                        ? 'transform scale-105' 
                                                        : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'
                                                    }
                                                `}
                                                style={{
                                                    transform: isSelected ? 'scale(1.05)' : 'scale(1)',
                                                    backgroundColor: isSelected ? '#fef7e0' : '',
                                                    borderColor: isSelected ? '#e7c22f' : ''
                                                }}
                                                onClick={(e) => {
                                                    // If already selected, prevent default and deselect
                                                    if (isSelected) {
                                                        e.preventDefault();
                                                        setValue(currentQuestion.id, '');
                                                        // Also manually uncheck the radio button
                                                        const radioInput = e.currentTarget.querySelector('input[type="radio"]');
                                                        if (radioInput) {
                                                            radioInput.checked = false;
                                                        }
                                                    }
                                                }}
                                            >
                                                <div 
                                                    className="flex-shrink-0 w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center transition-all duration-300"
                                                    style={{
                                                        borderColor: isSelected ? '#e7c22f' : '#d1d5db',
                                                        backgroundColor: isSelected ? '#e7c22f' : 'transparent',
                                                        transform: isSelected ? 'scale(1.05)' : 'scale(1)'
                                                    }}
                                                >
                                                    {isSelected && (
                                                        <svg className="w-2.5 h-2.5 text-white animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                        </svg>
                                                    )}
                                                </div>
                                                <span className={`text-sm transition-all duration-300 ${isSelected ? 'text-gray-900' : 'text-gray-700'}`}>
                                                    {option}
                                                </span>
                                                <input
                                                    type="radio"
                                                    value={option}
                                                    className="hidden"
                                                    {...register(currentQuestion.id, { required: currentQuestion.required ? 'Please select an option' : false })}
                                                />
                                            </label>
                                        );
                                    })}
                                </div>
                                {errors[currentQuestion.id] && (
                                    <p className="text-red-500 text-sm mt-2 text-left">{errors[currentQuestion.id].message}</p>
                                )}
                            </div>
                        )}

                        {/* Navigation Buttons */}
                        <div className="flex items-center justify-between">
                            <button
                                type="button"
                                onClick={handlePrevious}
                                className="text-sm font-medium transition-all duration-200 hover:scale-105"
                                style={{
                                    color: '#43213e'
                                }}
                            >
                                ← Back
                            </button>

                            <button
                                type="submit"
                                className="text-white font-semibold py-2 px-5 rounded-xl transition-all duration-200 text-sm hover:scale-105 transform"
                                style={{
                                    backgroundColor: '#43213e'
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#2f1829'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = '#43213e'}
                            >
                                {currentQuestionIndex === allQuestions.length - 1 ? 'Next →' : 'Next →'} 
                            </button>
                        </div>
                    </form>
                </div>

                {/* Collapsible Answer History - HIDDEN FOR NOW */}
                {false && submittedAnswers.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 mx-4">
                        <button
                            onClick={() => setShowAnswerHistory(!showAnswerHistory)}
                            className="w-full p-2.5 flex items-center justify-between text-left transition-all duration-200 hover:bg-gray-50"
                        >
                            <span className="text-gray-600 font-medium text-xs">Previous Answers ({submittedAnswers.length})</span>
                            {showAnswerHistory ? (
                                <ChevronUpIcon className="h-3 w-3 text-gray-400 transition-transform duration-200" />
                            ) : (
                                <ChevronDownIcon className="h-3 w-3 text-gray-400 transition-transform duration-200" />
                            )}
                        </button>
                        
                        {showAnswerHistory && (
                            <div className="px-2.5 pb-2.5 border-t border-gray-100 transition-all duration-300 ease-in-out">
                                <div className="space-y-1.5 mt-2 max-h-40 overflow-y-auto">
                                    {submittedAnswers.slice(-3).map((entry, index) => (
                                        <div key={index} className="text-xs">
                                            <p className="font-semibold text-gray-700 mb-0.5 text-left">
                                                {entry.question.length > 50 ? entry.question.substring(0, 50) + '...' : entry.question}
                                            </p>
                                            <p className="text-gray-500 text-left">{entry.answer}</p>
                                        </div>
                                    ))}
                                    {submittedAnswers.length > 3 && (
                                        <p className="text-xs text-gray-400 text-center">
                                            Showing last 3 answers
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}
                    </div>
            </div>

            {/* Sticky Progress Footer */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3">
                <div className="text-center">
                    <p className="text-gray-600 text-xs font-medium">
                        {remainingMinutes === 0 
                            ? 'Almost done!' 
                            : `About ${remainingMinutes} minute${remainingMinutes !== 1 ? 's' : ''} remaining`
                        }
                    </p>
                </div>
                
                {/* HIDDEN: Split Progress Bars and Section Labels */}
                {false && (
                    <>
                        {/* Split Progress Bars */}
                        <div className="flex gap-2 mb-2">
                            {/* Screening Questions Progress Bar */}
                            <div className="flex-1">
                                <div className="w-full bg-gray-200 rounded-full h-1.5">
                                    <div 
                                        className="h-full rounded-full transition-all duration-500 ease-out"
                                        style={{ 
                                            width: `${screeningProgress}%`,
                                            backgroundColor: '#43213e'
                                        }}
                                    ></div>
                                </div>
                            </div>
                            
                            {/* Personal Information Progress Bar */}
                            <div className="flex-1">
                                <div className="w-full bg-gray-200 rounded-full h-1.5">
                                    <div 
                                        className="h-full rounded-full transition-all duration-500 ease-out"
                                        style={{ 
                                            width: `${personalInfoProgress}%`,
                                            backgroundColor: '#43213e'
                                        }}
                                    ></div>
                                </div>
                            </div>
                        </div>
                        
                        {/* Section Labels */}
                        <div className="flex gap-2">
                            <div className="flex-1 text-center">
                                <span className="font-medium text-xs text-gray-500">Screening Questions</span>
                                <br />
                                <div className="flex items-center justify-center gap-1">
                                    {screeningProgress >= 100 && (
                                        <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                        </svg>
                                    )}
                                    <span className="text-xs text-gray-500">
                                        {formatSectionTime(screeningQuestionsRemainingSeconds, screeningProgress >= 100)}
                                    </span>
                                </div>
                            </div>
                            <div className="flex-1 text-center">
                                <span className="font-medium text-xs text-gray-500">Personal Information</span>
                                <br />
                                <div className="flex items-center justify-center gap-1">
                                    {personalInfoProgress >= 100 && (
                                        <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                        </svg>
                                    )}
                                    <span className="text-xs text-gray-500">
                                        {formatSectionTime(personalInfoRemainingSeconds, personalInfoProgress >= 100)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
} 